<template>
  <div class="service-panel">
    <div class="service-filters">
      <input
        ref="filterInput"
        v-model="query"
        type="search"
        class="service-filter"
        placeholder="Filter"
        aria-label="Filter service contacts"
      />
      <select v-model="typeFilter" class="service-type-filter" aria-label="Service type">
        <option value="">All types</option>
        <option v-for="type in SERVICE_TYPES" :key="type" :value="type">
          {{ serviceTypeLabel(type) }}
        </option>
      </select>
    </div>
    <div class="service-list">
      <p v-if="loading" class="service-status">Loading service contacts...</p>
      <p v-else-if="error" class="service-status">{{ error }}</p>
      <p v-else-if="!visible.length" class="service-status">No service contacts</p>
      <article
        v-for="row in visible"
        :key="row.id"
        class="service-card"
        @dblclick="openEdit(row)"
      >
        <div class="service-card-head">
          <h3>{{ row.businessName }}</h3>
          <a
            v-if="websiteHref(row.website)"
            class="service-web"
            :href="websiteHref(row.website)"
            target="_blank"
            rel="noopener"
            title="Open website"
            aria-label="Open website"
            @click.stop
            @dblclick.stop
          >
            <i class="fas fa-globe" aria-hidden="true"></i>
          </a>
          <span
            class="service-type-chip"
            :style="serviceTypeChipStyle(row.serviceType)"
          >{{ serviceTypeLabel(row.serviceType) }}</span>
        </div>
        <p v-if="row.website" class="service-websites">
          <template v-if="serviceWebsiteLinks(row.website).length">
            <a
              v-for="(link, index) in serviceWebsiteLinks(row.website)"
              :key="row.id + '-' + index"
              class="service-site-link"
              :href="link.href"
              target="_blank"
              rel="noopener"
              @click.stop
              @dblclick.stop
            >{{ link.label }}</a>
          </template>
          <span v-else>{{ row.website }}</span>
        </p>
        <p v-if="row.email">
          <a
            href="#"
            @click.prevent.stop="openEmail(row.email)"
            @dblclick.stop
          >{{ row.email }}</a>
        </p>
        <p v-if="phoneTelHref(row.businessPhone)" class="phone-line">
          <a
            class="service-phone"
            :href="phoneTelHref(row.businessPhone)"
            @click.stop
            @dblclick.stop
          >
            {{ phoneDisplay(row.businessPhone) }}
          </a>
          <button
            type="button"
            class="sms-open"
            title="SMS"
            @click.stop="openSms(row.businessPhone, row.businessName)"
            @dblclick.stop
          >
            <i class="fas fa-sms" aria-hidden="true"></i>
            SMS
          </button>
        </p>
        <p v-if="row.primaryContactName || phoneTelHref(row.primaryContactPhone)" class="phone-line">
          <span v-if="row.primaryContactName">{{ row.primaryContactName }}</span>
          <a
            v-if="phoneTelHref(row.primaryContactPhone)"
            class="service-phone"
            :href="phoneTelHref(row.primaryContactPhone)"
            @click.stop
            @dblclick.stop
          >{{ phoneDisplay(row.primaryContactPhone) }}</a>
          <button
            v-if="phoneTelHref(row.primaryContactPhone)"
            type="button"
            class="sms-open"
            title="SMS"
            @click.stop="openSms(row.primaryContactPhone, row.primaryContactName || row.businessName)"
            @dblclick.stop
          >
            <i class="fas fa-sms" aria-hidden="true"></i>
            SMS
          </button>
        </p>
        <p v-if="row.notes" class="service-notes">{{ row.notes }}</p>
      </article>
    </div>

    <div v-if="draft" class="service-overlay" @click.self="closeEdit">
      <form class="service-dialog" @submit.prevent="save">
        <div class="service-dialog-bar">
          <h3>{{ draft.id ? 'Edit service contact' : 'New service contact' }}</h3>
          <button type="button" class="dialog-close" @click="closeEdit">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="service-dialog-body">
          <label>Business name</label>
          <input
            ref="businessNameInput"
            v-model="draft.businessName"
            type="text"
            maxlength="128"
            required
          />
          <label>Type</label>
          <select v-model="draft.serviceType">
            <option v-for="type in SERVICE_TYPES" :key="type" :value="type">
              {{ serviceTypeLabel(type) }}
            </option>
          </select>
          <label>Web site</label>
          <textarea
            v-model="draft.website"
            rows="3"
            maxlength="255"
            placeholder="[site name](https://example.com)"
            @blur="formatDraftWebsite"
          ></textarea>
          <label>Email</label>
          <input v-model="draft.email" type="email" maxlength="255" />
          <label>Business phone</label>
          <input v-model="draft.businessPhone" type="tel" inputmode="tel" />
          <label>Primary contact name</label>
          <input v-model="draft.primaryContactName" type="text" maxlength="128" />
          <label>Primary contact phone</label>
          <input v-model="draft.primaryContactPhone" type="tel" inputmode="tel" />
          <label>Notes</label>
          <textarea v-model="draft.notes" rows="3" maxlength="4000"></textarea>
          <p v-if="saveError" class="service-save-error">{{ saveError }}</p>
        </div>
        <div class="service-dialog-actions">
          <button
            v-if="draft.id"
            type="button"
            class="delete-contact"
            :disabled="saving"
            @click="remove"
          >
            Delete
          </button>
          <button type="button" @click="closeEdit">Cancel</button>
          <button type="submit" class="save-contact" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/axiosConfig';
import eventBus from './eventBus.js';
import {
  SERVICE_TYPES,
  filterServiceContacts,
  phoneDigits,
  phoneDisplay,
  canonicalPhone,
  phoneTelHref,
  serviceTypeLabel,
  serviceTypeChipStyle,
  websiteHref,
  serviceWebsiteLinks,
  formatServiceWebsite,
  readServiceContactDraft,
  writeServiceContactDraft,
  clearServiceContactDraft,
} from '@/utils/serviceContacts';

const route = useRoute();
const router = useRouter();
const rows = ref([]);
const loading = ref(true);
const error = ref('');
const query = ref('');
const typeFilter = ref('');
const draft = ref(readServiceContactDraft(window.localStorage));
const filterInput = ref(null);
const businessNameInput = ref(null);
const saving = ref(false);
const saveError = ref('');

const visible = computed(() =>
  filterServiceContacts(rows.value, query.value, typeFilter.value),
);

watch(
  draft,
  (value) => {
    if (!value) return;
    writeServiceContactDraft(value, window.localStorage);
  },
  { deep: true },
);

function blankDraft() {
  return {
    id: null,
    businessName: '',
    serviceType: 'plumbing',
    website: '',
    email: '',
    businessPhone: '',
    primaryContactName: '',
    primaryContactPhone: '',
    notes: '',
  };
}

async function load() {
  error.value = '';
  try {
    const { data } = await axios.get('/api/service-contacts');
    rows.value = Array.isArray(data) ? data : [];
  } catch (err) {
    rows.value = [];
    error.value =
      err.response?.data?.error || 'Could not load service contacts';
  } finally {
    loading.value = false;
  }
}

function focusBusinessName() {
  nextTick(() => {
    const el = businessNameInput.value;
    if (el && typeof el.focus === 'function') el.focus();
  });
}

function focusFilter() {
  nextTick(() => {
    const el = filterInput.value;
    if (el && typeof el.focus === 'function') el.focus();
  });
}

function openNew() {
  saveError.value = '';
  draft.value = blankDraft();
  focusBusinessName();
}

function openEdit(row) {
  saveError.value = '';
  draft.value = {
    id: row.id,
    businessName: row.businessName || '',
    serviceType: row.serviceType || 'other',
    website: formatServiceWebsite(row.website),
    email: row.email || '',
    businessPhone: canonicalPhone(row.businessPhone),
    primaryContactName: row.primaryContactName || '',
    primaryContactPhone: canonicalPhone(row.primaryContactPhone),
    notes: row.notes || '',
  };
  focusBusinessName();
}

function formatDraftWebsite() {
  if (!draft.value) return;
  draft.value.website = formatServiceWebsite(draft.value.website);
}

onMounted(() => {
  if (draft.value) {
    formatDraftWebsite();
    focusBusinessName();
  } else focusFilter();
});

function closeEdit() {
  if (saving.value) return;
  draft.value = null;
  saveError.value = '';
}

async function save() {
  if (!draft.value || saving.value) return;
  saving.value = true;
  saveError.value = '';
  draft.value.businessPhone = canonicalPhone(draft.value.businessPhone);
  draft.value.primaryContactPhone = canonicalPhone(
    draft.value.primaryContactPhone,
  );
  formatDraftWebsite();
  const body = { ...draft.value };
  const id = body.id;
  delete body.id;
  try {
    if (id) {
      await axios.put(`/api/service-contacts/${id}`, body);
    } else {
      await axios.post('/api/service-contacts', body);
    }
    clearServiceContactDraft(window.localStorage);
    draft.value = null;
    await load();
  } catch (err) {
    saveError.value =
      err.response?.data?.error || 'Could not save that service contact';
  } finally {
    saving.value = false;
  }
}

async function remove() {
  if (!draft.value || !draft.value.id || saving.value) return;
  const ok = window.confirm(`Delete ${draft.value.businessName || 'this contact'}?`);
  if (!ok) return;
  saving.value = true;
  saveError.value = '';
  try {
    await axios.delete(`/api/service-contacts/${draft.value.id}`);
    clearServiceContactDraft(window.localStorage);
    draft.value = null;
    await load();
  } catch (err) {
    saveError.value =
      err.response?.data?.error || 'Could not delete that service contact';
  } finally {
    saving.value = false;
  }
}

function openEmail(email) {
  const to = String(email || '').trim();
  if (!to) return;
  if (route.path === '/mproperties') {
    router.push({ path: '/quickemail', query: { composeTo: to } });
    return;
  }
  eventBus.emit('showQuickEmail');
  nextTick(() => {
    eventBus.emit('openQuickEmail', { to, subject: '', body: '' });
  });
}

function openSms(phone, name) {
  const digits = phoneDigits(phone);
  if (!digits) return;
  const payload = { phoneDigits: digits, name: String(name || '').trim() };
  if (route.path === '/mproperties') {
    router.push({
      path: '/msms',
      query: { composePhone: digits, composeName: payload.name },
    });
    return;
  }
  eventBus.emit('openSmsCompose', payload);
}

load();

defineExpose({ reload: load, openNew });
</script>

<style scoped>
.service-panel {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f3f3f3;
}

.service-filters {
  display: flex;
  gap: 8px;
  padding: 10px 10px 0;
  flex: 0 0 auto;
}

.service-filter,
.service-type-filter,
.service-dialog input,
.service-dialog select,
.service-dialog textarea {
  font: inherit;
  font-size: 0.9em;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  box-sizing: border-box;
  background: #fff;
}

.service-filter {
  flex: 1;
  min-width: 0;
}

.service-type-filter {
  max-width: 42%;
}

.service-list {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 10px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.service-list::-webkit-scrollbar {
  display: none;
}

.service-status {
  color: #777;
  font-size: 0.85em;
  margin: 8px;
}

.service-card {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: pointer;
}

.service-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.service-card h3 {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 0.95em;
  color: #333;
  overflow-wrap: anywhere;
}

.service-type-chip {
  flex: 0 0 auto;
  max-width: 48%;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.72em;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.service-card p {
  margin: 4px 0;
  font-size: 0.85em;
  color: #333;
  overflow-wrap: anywhere;
}

.service-card a {
  color: #1565c0;
}

.service-websites {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
}

.service-card a.service-site-link {
  color: #1565c0;
  text-decoration: underline;
}

.service-card a.service-phone {
  display: inline-block;
  text-decoration: none;
  background: #d6ebff;
  border-radius: 3px;
  padding: 0 4px;
  color: #1a4f9c;
  white-space: nowrap;
}

.service-card a.service-phone:hover {
  background: #c5e0fb;
}

.service-card a.service-web {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-top: 1px;
  border-radius: 50%;
  background: #d6ebff;
  color: #1a4f9c;
  text-decoration: none;
  font-size: 0.8em;
}

.service-card a.service-web:hover {
  background: #c5e0fb;
}

.phone-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sms-open {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #d4c4d8;
  background: #ede4f0;
  color: #6e5875;
  border-radius: 3px;
  padding: 0 6px;
  font-size: 0.85em;
  line-height: 1.4;
  cursor: pointer;
}

.sms-open i {
  font-size: 0.9em;
}

.service-notes {
  color: #555;
  white-space: pre-wrap;
}

.service-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.service-dialog {
  width: 360px;
  max-width: 94%;
  max-height: 100%;
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.service-dialog-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #26c6da;
  color: #fff;
  padding: 10px 12px;
}

.service-dialog-bar h3 {
  margin: 0;
  font-size: 0.95em;
}

.dialog-close {
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.service-dialog-body {
  overflow-y: auto;
  padding: 12px;
  min-height: 0;
}

.service-dialog label {
  display: block;
  margin: 10px 0 4px;
  font-size: 0.8em;
  color: #555;
}

.service-dialog label:first-child {
  margin-top: 0;
}

.service-dialog input,
.service-dialog select,
.service-dialog textarea {
  width: 100%;
}

.service-save-error {
  color: #c62828;
  font-size: 0.85em;
  margin: 8px 0 0;
}

.service-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #eee;
}

.service-dialog-actions button {
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}

.delete-contact {
  margin-right: auto;
  color: #c62828;
}

.service-dialog-actions button.save-contact {
  background: #907793;
  border-color: #907793;
  color: #fff;
}
</style>
